"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { ChevronRightIcon } from "@/components/appIcons";
import { cn } from "@/lib/utils";

interface ScrollFadeProps {
  children: ReactNode;
  className?: string;
  /** For rows that never wrap: keep the fades at every width and show a "scroll right" button. */
  scrollHint?: boolean;
}

/**
 * Horizontal scroll container with hidden scrollbar and gradient fade overlays.
 * On mobile: content scrolls horizontally, gradients hint at more content.
 * On desktop (sm+): gradients are hidden (assumes content wraps via sm:flex-wrap),
 * unless `scrollHint` is set for rows that never wrap.
 */
export function ScrollFade({ children, className, scrollHint = false }: ScrollFadeProps) {
  const t = useTranslations("globals.accessibility");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    function update() {
      const { scrollLeft, scrollWidth, clientWidth } = el!;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollWidth - clientWidth - scrollLeft > 1);
    }

    update();
    el.addEventListener("scroll", update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener("scroll", update);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  const scrollRight = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: Math.round(el.clientWidth * 0.7), behavior: "smooth" });
  };

  return (
    <div className={cn("relative", className)}>
      {/* Left fade — visible when scrolled past start */}
      <div
        className={cn(
          "pointer-events-none absolute left-0 top-0 bottom-0 w-6 z-10",
          !scrollHint && "sm:hidden",
          "bg-gradient-to-r from-background to-transparent",
          "transition-opacity",
          canScrollLeft ? "opacity-100" : "opacity-0",
        )}
      />
      {/* Scrollable content */}
      <div ref={scrollRef} className="overflow-x-auto no-scrollbar">
        {children}
      </div>
      {/* Right fade — visible when more content to scroll */}
      <div
        className={cn(
          "pointer-events-none absolute right-0 top-0 bottom-0 z-10",
          !scrollHint && "sm:hidden",
          "bg-gradient-to-l from-background to-transparent",
          "transition-opacity",
          scrollHint ? "w-14" : "w-6",
          canScrollRight ? "opacity-100" : "opacity-0",
        )}
      />
      {scrollHint && canScrollRight && (
        <button
          type="button"
          onClick={scrollRight}
          aria-label={t("scrollRight")}
          className="absolute right-0 top-1/2 z-20 flex size-8 -translate-y-1/2 after:absolute after:-inset-1.5 after:content-[''] items-center justify-center rounded-full border border-border bg-card text-foreground shadow-md"
        >
          <ChevronRightIcon className="text-[14px]" />
        </button>
      )}
    </div>
  );
}
